import { SmwTableComponent } from './smw-table.component';

import { AuthService, Season } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import { SmwService, StationInfo } from './smw.service';

import { DatexPipe } from '../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { CsvModule } from '@ctrl/ngx-csv';
import { Moment } from 'moment';
import moment from 'moment';
import { startWith } from 'rxjs';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatCardModule,
  MatFormFieldModule,
  MatDatepickerModule,
  ReactiveFormsModule,
  MatButtonModule,
  CsvModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent, SmwTableComponent];
export const PIPES = [DatexPipe];

@Component({
  selector: 'app-smw-archive',
  templateUrl: './smw-archive.component.html',
  styleUrls: ['./smw-archive.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS,
    },
  ],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class SmwArchiveComponent implements OnInit {
  private injector = inject(Injector);
  private adapter = inject(DateAdapter<any>);
  private auth = inject(AuthService);
  private smwService = inject(SmwService);
  public translate = inject(TranslateService);
  public languageService = inject(LanguageService);

  stationId = input();
  stationInfo = input<StationInfo>();

  previousStationId = signal(null);
  range = new UntypedFormGroup({
    start: new UntypedFormControl(null, Validators.required),
    end: new UntypedFormControl(null, Validators.required),
  });

  private formStatus = toSignal(this.range.statusChanges, {
    initialValue: this.range.status,
  });

  rangeInvalid = computed(() => this.formStatus() === 'INVALID');

  private startStatusSig = toSignal(
    this.range.controls.start.statusChanges.pipe(
      startWith(this.range.controls.start.status)
    )
  );

  private endStatusSig = toSignal(
    this.range.controls.end.statusChanges.pipe(
      startWith(this.range.controls.end.status)
    )
  );

  startValueSig = toSignal(
    this.range.controls.start.valueChanges.pipe(
      startWith(this.range.controls.start.value)
    )
  );

  endValueSig = toSignal(
    this.range.controls.end.valueChanges.pipe(
      startWith(this.range.controls.end.value)
    )
  );

  startIsValid = computed(() => {
    const status = this.startStatusSig();
    return status === 'VALID';
  });

  endIsValid = computed(() => {
    const status = this.endStatusSig();
    return status === 'VALID';
  });

  startHasMatError = computed(() => {
    this.startStatusSig();
    return this.range.controls.start.hasError('matStartDateInvalid');
  });

  endHasMatError = computed(() => {
    this.endStatusSig();
    return this.range.controls.end.hasError('matEndDateInvalid');
  });

  startHasMatRequiredError = computed(() => {
    this.startStatusSig();
    return this.range.controls.start.hasError('required');
  });

  endHasMatRequiredError = computed(() => {
    this.endStatusSig();
    return this.range.controls.end.hasError('required');
  });

  allowedDates = [];
  smwData = signal<any>(undefined);
  csvData = [];
  csvHeader = [];
  csvFileName = '';

  constructor() {
    this.listenToStationIdChanges();
  }

  dateFilter = (d: Moment | null): boolean => {
    if (this.isCustomerMaster() && this.isDateSameOrBeforeToday(d)) {
      return true;
    }
    return this.allowedDates.indexOf(d.unix()) >= 0;
  };

  ngOnInit(): void {
    this.adapter.setLocale(this.auth.getLanguage());
  }

  getDatesForStation(stationId): void {
    const dates = [];
    const today: Moment = moment(moment().format('YYYY-MM-DD 00:00:00'));
    const customerDocument = this.auth.getCustomerDocument();
    if (customerDocument.hasOwnProperty('smwProducts')) {
      // all smw products
      for (const product of customerDocument['smwProducts']) {
        // was it ever active?
        if (this.auth.isProductActive('smwProducts', product.id, true)) {
          // does it contain current station?
          if (
            product['stations'].filter(s => s.stationId == stationId).length > 0
          ) {
            const startDate = moment(
              moment(product['contractStartDtg']).format('YYYY-MM-DD 00:00:00')
            );
            let endDate = today;
            if (product['contractExpiryDtg']) {
              endDate = moment(
                moment(product['contractExpiryDtg']).format(
                  'YYYY-MM-DD 00:00:00'
                )
              );
              if (endDate.isAfter(today)) {
                endDate = today;
              }
            }
            endDate.add(1, 'day');
            // check each day between start and expiry date
            for (
              const dtg = startDate;
              dtg.isBefore(endDate);
              dtg.add(1, 'day')
            ) {
              const timeStamp = dtg.unix();
              if (dates.indexOf(timeStamp) < 0) {
                // check season
                if (this.isDateInSeason(dtg, product['saison'])) {
                  dates.push(timeStamp);
                }
              }
            }
          }
        }
      }
    }
    this.allowedDates = dates;
  }

  isDateInSeason(day: Moment, season: Season): boolean {
    if (!season) {
      return true;
    }
    // convert to moment
    const start: Moment = moment({
      year: day.year(),
      month: Number(season.start.month) - 1,
      day: Number(season.start.day),
    });
    const end: Moment = moment({
      year: day.year(),
      month: Number(season.end.month) - 1,
      day: Number(season.end.day),
    });

    if (start.isSameOrBefore(end)) {
      // start <= end, z.B. 01.04.-30.10.
      return !(day.isBefore(start) || day.isAfter(end));
    }
    // start > end, z.B. 01.11.-30.04.
    if (day.isSameOrAfter(start) || day.isSameOrBefore(end)) {
      // z.B. today = 05.12. || today = 24.03
      return true;
    }
    return false;
  }

  onQueryData(): void {
    this.getSmwData();
  }

  getSmwData(): void {
    this.smwData.set(null);
    const dtgEnd = moment(this.range.value.end).add(24, 'hours');
    this.smwService
      .getSmwData(this.stationId(), this.range.value.start, dtgEnd)
      .subscribe(
        data => {
          this.smwData.set([...data]);
        },
        error => console.log('Error SMW Archive getSmwData', error)
      );
  }

  /*
  'webcam', 'tt', 'td', 'freezing_tt', 'freezing_tt2', 't5', 'tg0', 'tg5', 'tg5_2', 'tg10', 'tg20', 'tg30', 'tg30_2', 'tg50', 'tg100',
      'road_tt', 'road_tt2', 'road_tt3', 'road_condition', 'road_condition2', 'road_condition3', 'rh',
      'rrr', 'rrr_day', 'rrr_week', 'rrr_month', 'rrr_year', 'rr', 'r_avail', 'r_intensity', 'r_type',
      'ww', 'snow_height', 'dd', 'ff', 'ddg', 'ffg', 'fg', 'wft', 'salt', 'salt2', 'ss1', 'vis', 'vv', 'ppp', 'qfe', 'road_grip', 'n'
   */
  onCreateCSV(): void {
    const csvData = [];
    const parameters = this.extractParametersFromData();
    this.csvHeader = this.createCsvHeaders(parameters);
    const stationInfo = this.stationInfo();
    this.csvFileName = this.cleanFileName(
      'einsatzwetter-messwerte-' +
        this.stationId() +
        '-' +
        stationInfo.name +
        this.range.value.start.format('YYYYMMDD') +
        '_' +
        this.range.value.end.format('YYYYMMDD') +
        '.csv'
    );
    for (const smw of this.smwData()) {
      const csvDataset = {
        dtg: moment(smw['dtg']).format('DD.MM.YYYY HH:mm'),
      };
      for (const parameter of parameters) {
        let value: any = -9999;
        if (smw.hasOwnProperty(parameter)) {
          value = smw[parameter];
        }
        if (value !== -9999) {
          switch (parameter) {
            case 'road_condition':
            case 'road_condition2':
            case 'road_condition3':
              value = this.translate.instant(
                'MAPPING.GMA_ROAD_CONDITION.' + value
              );
              break;
            case 'ww':
              value = this.translate.instant(
                'MAPPING.WW.' + this.smwService.getCleanedWW(value)
              );
              break;
            case 'r_type':
              value = this.translate.instant(
                this.smwService.gmaPrecipitationTypeMapping(
                  value,
                  stationInfo.type
                )
              );
              break;
            case 'dd':
            case 'ddg':
              const factor: number = 360 / 8;
              const dd =
                'WINDROSE.' +
                Math.floor((value + factor / 2) / factor) * factor;
              value = this.translate.instant(dd);
              break;
            case 'vis':
            case 'vv':
              value = value / 1000;
              break;
            case 'gmaAlarm':
              value = this.smwService.getGmaAlarm(smw);
              break;
          }
        }
        csvDataset[parameter] =
          value === -9999 ? '' : String(value).replace('.', ',');
      }
      csvData.push(csvDataset);
    }
    this.csvData = csvData;
  }

  cleanFileName(fileName: string): string {
    const replaceStrings = {
      ä: 'ae',
      ö: 'oe',
      ü: 'ue',
      ß: 'ss',
    };
    let cleanedFileName = fileName.toLowerCase().trim();
    for (const specialChar of Object.keys(replaceStrings)) {
      cleanedFileName = cleanedFileName.replace(
        specialChar,
        replaceStrings[specialChar]
      );
    }
    return cleanedFileName;
  }
  createCsvHeaders(parameters: Array<string>): Array<any> {
    const headers = [
      { label: this.translate.instant('METPARA.Dtg'), key: 'dtg' },
    ];
    for (const parameter of parameters) {
      headers.push({
        label: this.translate.instant(
          'PRODUCT.SMW.TABLE_PARAMETERS.' + parameter
        ),
        key: parameter,
      });
    }
    return headers;
  }

  extractParametersFromData(): Array<string> {
    const parametersFromData = [];
    if (this.stationInfo().gmaAlarm) {
      parametersFromData.push('gmaAlarm');
    }
    for (const dataset of this.smwData()) {
      for (const parameter of Object.keys(dataset)) {
        if (parameter === 'gmaAlarm') {
          continue;
        }
        if (
          parameter !== 'dtg' &&
          parameter !== 'id_station' &&
          parametersFromData.indexOf(parameter) < 0
        ) {
          parametersFromData.push(parameter);
        }
      }
    }
    const parameters = [];
    for (const parameter of this.smwService.allParameters) {
      if (parametersFromData.indexOf(parameter) >= 0) {
        parameters.push(parameter);
      }
    }
    return parameters;
  }

  isCustomerMaster(): boolean {
    const customerDocument = this.auth.getCustomerDocument();
    return (
      customerDocument.hasOwnProperty('isMaster') &&
      customerDocument['isMaster']
    );
  }

  isDateSameOrBeforeToday(d: Moment | null): boolean {
    const today: Moment = moment(moment().format('YYYY-MM-DD 00:00:00'));
    return d.isSameOrBefore(today);
  }

  private listenToStationIdChanges() {
    effect(
      () => {
        const stationId = this.stationId();

        untracked(() => {
          if (!isNil(stationId) && this.previousStationId() != stationId) {
            this.smwData.set(null);
            this.range.controls['start'].setValue(null);
            this.range.controls['end'].setValue(null);
            this.getDatesForStation(stationId);
          }
          this.previousStationId.set(stationId);
        });
      },
      { injector: this.injector }
    );
  }
}
